import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Trophy, Zap, Download, MessageCircle, ArrowLeft, ArrowRight, Sparkles, ShieldCheck,
  Hammer, Brain, Compass, Calendar, Target, Award, FileText, Image as ImageIcon,
  HelpCircle, GraduationCap, ExternalLink, Lock, Unlock, RefreshCw
} from 'lucide-react';
import { useQuest } from '@/context/QuestContext';
import { api, track } from '@/lib/api';
import { ZONES } from '@/lib/quizData';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import { BrandBanner } from '@/components/BrandLogo';

const UNLOCK_URL = 'https://futureready.school/careerclaritykit';
const DASHBOARD_URL = process.env.REACT_APP_DASHBOARD_URL || 'https://futureready.school/dashboard';

export default function Result() {
  const navigate = useNavigate();
  const { session } = useQuest();
  const result = session.result;
  const cardRef = useRef(null);
  const [whatsapp, setWhatsapp] = useState('');
  const [coursesByZone, setCoursesByZone] = useState({});
  const [premium, setPremium] = useState(true); // invite-only paywall retired — every report is unlocked
  const [checkingPremium, setCheckingPremium] = useState(false);
  const [fullReportUrl, setFullReportUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [pdfRetrying, setPdfRetrying] = useState(false);

  const checkPremium = React.useCallback(async (opts = { silent: false }) => {
    if (!session.leadId) return;
    if (!opts.silent) setCheckingPremium(true);
    try {
      const r = await api.get(`/leads/${session.leadId}/status`);
      const isPrem = !!r.data.premium;
      setPremium((prev) => {
        if (!prev && isPrem && !opts.silent) toast.success('Full report unlocked! 🎉');
        return isPrem;
      });
      if (r.data.full_report_url) {
        setFullReportUrl(r.data.full_report_url);
        setPdfError(null);
      } else if (r.data.pdf_error) {
        setPdfError(r.data.pdf_error);
      }
    } catch (e) {
      console.warn('[premium] status check failed:', e?.message || e);
    } finally {
      if (!opts.silent) setCheckingPremium(false);
    }
  }, [session.leadId]);

  const retryReport = async () => {
    if (!session.leadId || pdfRetrying) return;
    setPdfRetrying(true);
    setPdfError(null);
    try {
      // Ask backend to regenerate the report. Uses the admin-resend endpoint
      // openly? No — instead we re-post to a public resend hook. Fallback:
      // hit the /leads/{id}/regenerate endpoint if it exists, else show a
      // toast asking the user to refresh.
      await api.post(`/leads/${session.leadId}/regenerate`);
      toast.success('Retrying — your PDF will appear here in a few seconds.');
      // Poll aggressively for the next 60s so the button flips fast.
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        await checkPremium({ silent: true });
        if (tries >= 15) clearInterval(iv);
      }, 4000);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Please try again in a minute.';
      toast.error(`Couldn't retry: ${detail}`);
    } finally {
      setPdfRetrying(false);
    }
  };

  useEffect(() => {
    if (!result || !session.player) {
      navigate('/');
      return;
    }
    track('result_viewed', { primary_zone: result.primary_zone?.id }, session.leadId);
    api.get('/config/whatsapp').then((r) => setWhatsapp(r.data.number)).catch(() => {});
    api.get('/courses').then((r) => setCoursesByZone(r.data.courses || {})).catch(() => {});
    checkPremium({ silent: true });
    // Poll every 6s for up to 5 minutes. First-boot chromium install on a
    // fresh prod pod can add ~90s to the very first PDF generation, so a
    // 2-minute cap was leaving users stuck on "Preparing your PDF…".
    const iv = setInterval(() => checkPremium({ silent: true }), 6000);
    const stopTimeout = setTimeout(() => clearInterval(iv), 5 * 60 * 1000);
    return () => { clearInterval(iv); clearTimeout(stopTimeout); };
    // Effect intentionally runs once on mount. `checkPremium` is stable via useCallback.
  }, []);

  if (!result || !session.player) return null;

  const primary = result.top_zones[0];
  const secondary = result.top_zones[1];
  const exploration = result.top_zones[2];

  const onUnlockClick = () => {
    track('unlock_full_report_clicked', { lead_id: session.leadId }, session.leadId);
    api.patch(`/leads/${session.leadId}`, { cta_clicked: true }).catch(() => {});
    window.open(UNLOCK_URL, '_blank', 'noopener,noreferrer');
  };

  const onWhatsApp = () => {
    track('whatsapp_clicked', {}, session.leadId);
    api.patch(`/leads/${session.leadId}`, { whatsapp_clicked: true }).catch(() => {});
    const msg = encodeURIComponent(`Hi, I just completed my Career Fit Assessment. My Career Avatar is ${result.avatar.name} and my Primary Career Zone is ${primary.name}. I'd like to discuss my 1-year roadmap with my mentor.`);
    window.open(`https://wa.me/${whatsapp || '919818202224'}?text=${msg}`, '_blank');
  };

  const downloadPNG = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 2, useCORS: true });
      const link = document.createElement('a');
      link.download = `career-quest-${session.player.student_name.replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      track('result_downloaded', { format: 'png' }, session.leadId);
      toast.success('Result card downloaded!');
    } catch (e) {
      toast.error('Could not generate image. Please try again.');
    }
  };

  const downloadPDF = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const ratio = canvas.height / canvas.width;
      const w = pageWidth - 60;
      const h = w * ratio;
      pdf.addImage(imgData, 'PNG', 30, 30, w, h);
      pdf.save(`career-quest-${session.player.student_name.replace(/\s+/g, '-')}.pdf`);
      track('result_downloaded', { format: 'pdf' }, session.leadId);
      toast.success('PDF downloaded!');
    } catch (e) {
      toast.error('Could not generate PDF. Please try again.');
    }
  };

  return (
    <div className="min-h-screen gradient-bg grain pb-16">
      <BrandBanner />
      <nav className="sticky top-0 z-30 glass">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" data-testid="result-home-link" className="flex items-center gap-2 text-sm font-bold text-muted-ink hover:text-ink">
            <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Home
          </Link>
          <div className="font-display font-extrabold text-ink text-sm">Your Future Profile</div>
          <div className="w-16" />
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-5 pt-8 space-y-6">
        {/* Header greeting */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0055FF]">Career Fit Assessment result</p>
          <h1 className="font-display font-black text-ink text-3xl sm:text-5xl mt-1 tracking-tighter">
            Hi {session.player.student_name.split(' ')[0]}, your result is ready 🎉
          </h1>
        </motion.div>

        {/* Avatar Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="card-soft overflow-hidden relative"
          style={{ background: `linear-gradient(135deg, ${primary.color}1A 0%, #ffffff 60%)` }}
        >
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full opacity-20" style={{ background: primary.color }} />
          <div className="relative p-7 sm:p-10 grid sm:grid-cols-5 gap-6 items-center">
            <div className="sm:col-span-3">
              <div className="chip" style={{ borderColor: `${primary.color}40` }}>
                <Sparkles className="w-3.5 h-3.5" style={{ color: primary.color }} strokeWidth={3} />
                <span>Your Career Avatar</span>
              </div>
              <h2 className="font-display font-black text-ink text-4xl sm:text-5xl mt-3 tracking-tighter">{result.avatar.name}</h2>
              <p className="text-body mt-3 text-base sm:text-lg max-w-lg">{result.avatar.line}</p>
            </div>
            <div className="sm:col-span-2 flex justify-center">
              <div className="w-44 h-44 sm:w-56 sm:h-56 rounded-3xl overflow-hidden shadow-2xl relative" style={{ background: `linear-gradient(145deg, ${primary.color}33, #ffffff)` }}>
                {result.avatar.image ? (
                  <img src={result.avatar.image} alt={result.avatar.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(145deg, ${primary.color}, #0F172A)` }}>
                    <Trophy className="w-24 h-24 text-white" strokeWidth={2} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Guidance signal card — only when user selected "None of the above" > 40% */}
        {result.uncertainty_pct > 40 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="card-soft p-6 sm:p-7 border-2"
            style={{ borderColor: result.guidance_level === 'High' ? '#FF5A00' : result.guidance_level === 'Medium' ? '#FFD600' : '#0055FF' }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shrink-0"
                   style={{ background: result.guidance_level === 'High' ? '#FF5A00' : result.guidance_level === 'Medium' ? '#E0A800' : '#0055FF' }}>
                <HelpCircle className="w-7 h-7" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-ink">Heads-up signal</div>
                <div className="font-display font-black text-ink text-xl sm:text-2xl mt-1 tracking-tight">
                  You picked &ldquo;None of the above&rdquo; {result.uncertainty_count} times ({result.uncertainty_pct}%)
                </div>
                <p className="text-body mt-2 text-sm sm:text-base">
                  That&apos;s a lot of uncertainty — and that&apos;s totally okay at this stage. Explore your top zones below and pick the one that resonates most.
                </p>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-display font-extrabold"
                     style={{ background: '#0F172A', color: '#fff' }}>
                  Guidance need: High
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Top 3 Career Zones */}
        <section>
          <SectionTitle index="01" title="Your Top 3 Career Zones" icon={Compass} />
          <div className="grid lg:grid-cols-3 gap-4 mt-5">
            {[primary, secondary, exploration].map((z, i) => (
              <motion.div
                key={z.id}
                initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card-soft p-6 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: z.color }} />
                <div className="flex items-center gap-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: z.color }}>
                    {['Primary', 'Secondary', 'Exploration'][i]}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-ink">#{i + 1}</div>
                </div>
                <h3 className="font-display font-black text-ink text-xl mt-2 tracking-tight">{z.name}</h3>
                <p className="text-sm text-body mt-2">{z.short}</p>
                <div className="mt-4 text-xs uppercase tracking-[0.18em] font-bold text-muted-ink">Possible careers</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {z.careers.map((c) => <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-[#F1F5F9] text-ink font-bold">{c}</span>)}
                </div>
                <div className="mt-4 text-xs uppercase tracking-[0.18em] font-bold text-muted-ink">Mini action step</div>
                <p className="text-sm text-body mt-1">{z.action}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Strength Profile — UNLOCKED for all users */}
        <section>
          <SectionTitle index="02" title="Your Strength Profile" icon={Brain} />
          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            {result.top_traits.map((t, i) => (
              <div key={t.name} className="card-soft p-5 flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center font-display font-black text-white text-sm shrink-0"
                     style={{ background: ['#0070fb', '#cd1d80', '#e59501', '#2e8606'][i] }}>{i + 1}</div>
                <div>
                  <div className="font-display font-extrabold text-ink text-base">{t.name}</div>
                  <div className="text-sm text-muted-ink mt-1">{t.description}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Skill Stack — LOCKED in partial */}
        <section>
          <SectionTitle index="03" title="Your Future Skill Stack" icon={Zap} locked={!premium} />
          <LockedSection locked={!premium} onUnlock={onUnlockClick} teaser={
            <p className="text-sm text-muted-ink">5 future-ready skills to start building this year, personalised for your top zone.</p>
          }>
          <div className="card-soft p-6 mt-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {result.skill_stack.map((s, i) => (
                <div key={s} className="rounded-2xl border-2 border-dashed border-[#E2E8F0] p-4 text-center">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0070fb]">Skill {i + 1}</div>
                  <div className="font-display font-extrabold text-ink mt-1 text-sm">{s}</div>
                </div>
              ))}
            </div>
          </div>
          </LockedSection>
        </section>

        {/* Mini Project — LOCKED in partial */}
        <section>
          <SectionTitle index="04" title="Your Mini Project Idea" icon={Hammer} locked={!premium} />
          <LockedSection locked={!premium} onUnlock={onUnlockClick} teaser={
            <p className="text-sm text-muted-ink">A hands-on project brief designed for your primary career zone.</p>
          }>
          <div className="card-dark p-7 mt-5">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#e59501]">Start this month</p>
            <h3 className="font-display font-black text-2xl sm:text-3xl mt-2 tracking-tight">{result.mini_project}</h3>
            <p className="text-sm text-white/70 mt-3">Designed for a {primary.name}. Build, share, then refine.</p>
          </div>
          </LockedSection>
        </section>

        {/* Roadmap */}
        <section>
          <SectionTitle index="05" title="Your 1-Year Exploration Roadmap" icon={Calendar} locked={!premium} />
          <LockedSection locked={!premium} onUnlock={onUnlockClick} teaser={
            <p className="text-sm text-muted-ink">A 4-phase plan across the next 30 days, 3 months, 6 months and 12 months — personalised for your primary zone.</p>
          }>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
            {[
              { t: 'Next 30 days', d: `Learn one beginner skill: ${result.skill_stack[0]}.` },
              { t: 'Next 3 months', d: `Build a mini project related to ${primary.name}.` },
              { t: 'Next 6 months', d: 'Present your project to peers or publish it online.' },
              { t: 'Next 12 months', d: 'Create a small portfolio with 2–3 projects + learnings.' },
            ].map((step, i) => (
              <div key={i} className="card-soft p-5 relative overflow-hidden">
                <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: ['#FFD600', '#0055FF', '#00D95F', '#FF5A00'][i] }} />
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-ink mt-1">Phase {i + 1}</div>
                <div className="font-display font-extrabold text-ink mt-1">{step.t}</div>
                <div className="text-sm text-body mt-2">{step.d}</div>
              </div>
            ))}
          </div>
          </LockedSection>
        </section>

        {/* Recommended Coursera Courses — UNLOCKED for all users */}
        <section>
          <SectionTitle index="06" title="Recommended Coursera Courses" icon={GraduationCap} />
          <p className="text-sm text-muted-ink mt-2">Hand-picked courses for your top career zones. Tap any course to start learning on Coursera.</p>
          <div className="space-y-4 mt-5">
            {[primary, secondary, exploration].map((z, i) => {
              const list = coursesByZone[z.id] || [];
              if (list.length === 0) return null;
              return (
                <div key={z.id} className="card-soft p-6 relative overflow-hidden" data-testid={`courses-zone-${z.id}`}>
                  <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: z.color }} />
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white shrink-0" style={{ background: z.color }}>
                      <GraduationCap strokeWidth={2.5} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: z.color }}>
                        {['Primary', 'Secondary', 'Exploration'][i]} Zone
                      </div>
                      <div className="font-display font-extrabold text-ink text-lg tracking-tight">{z.name}</div>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                    {list.map((c, ci) => (
                      <a
                        key={ci}
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`course-link-${z.id}-${ci}`}
                        onClick={() => track('course_clicked', { zone: z.id, title: c.title, url: c.url }, session.leadId)}
                        className="group flex items-start gap-3 p-4 rounded-2xl bg-[#F8FAFC] hover:bg-white border-2 border-transparent hover:border-[#0055FF] transition-all"
                      >
                        <div className="w-9 h-9 rounded-xl bg-white shadow-sm flex items-center justify-center shrink-0">
                          <GraduationCap className="w-4 h-4 text-[#0055FF]" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-extrabold text-ink text-sm leading-tight">{c.title}</div>
                          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-ink mt-1 flex items-center gap-1">
                            {c.provider || 'Coursera'} <ExternalLink className="w-3 h-3" strokeWidth={2.5} />
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
            {Object.keys(coursesByZone).length === 0 && (
              <div className="card-soft p-6 text-center text-sm text-muted-ink">Loading recommendations…</div>
            )}
          </div>
        </section>

        {/* Shareable Card */}
        <section>
          <SectionTitle index="07" title="Your Shareable Result Card" icon={Target} />
          <p className="text-sm text-muted-ink mt-2">Download as image or PDF to share with your parents.</p>

          <div className="mt-5">
            <div ref={cardRef} className="rounded-3xl overflow-hidden relative" style={{ background: `linear-gradient(135deg, ${primary.color} 0%, #0F172A 100%)`, padding: '36px', color: '#fff', width: '100%', maxWidth: 560, margin: '0 auto' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-2xl bg-white text-[#0055FF] flex items-center justify-center font-display font-black">CF</div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/70">Future Ready School</div>
                    <div className="font-display font-extrabold">Career Fit Assessment</div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/70">Career Avatar</div>
              </div>

              <div className="mt-6 flex items-center gap-4">
                {result.avatar.image && (
                  <div className="w-24 h-24 rounded-2xl overflow-hidden shrink-0 bg-white/10 ring-2 ring-white/20">
                    <img src={result.avatar.image} alt={result.avatar.name} className="w-full h-full object-cover" crossOrigin="anonymous" />
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">I am</p>
                  <div className="font-display font-black text-2xl sm:text-3xl mt-1 tracking-tighter">{result.avatar.name}</div>
                </div>
              </div>
              <p className="text-sm text-white/85 mt-3 max-w-md">{result.avatar.line}</p>

              <div className="mt-6">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">Top Career Zones</p>
                <ol className="mt-2 space-y-1 text-base font-display font-extrabold">
                  <li>1. {primary.name}</li>
                  <li>2. {secondary.name}</li>
                  <li>3. {exploration.name}</li>
                </ol>
              </div>

              <div className="mt-5">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">Future Ready Skill</p>
                <div className="font-display font-extrabold mt-1">{result.skill_stack[0]}</div>
              </div>

              <div className="mt-7 flex items-center justify-between text-xs text-white/60">
                <span>{session.player.student_name} · Grade {session.player.grade}</span>
                <span>futureready.school</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-5 max-w-xl mx-auto">
            <button onClick={downloadPNG} data-testid="download-png-button" className="btn-primary flex-1 inline-flex items-center justify-center gap-2">
              <ImageIcon className="w-4 h-4" strokeWidth={3} /> Download PNG
            </button>
            <button onClick={downloadPDF} data-testid="download-pdf-button" className="btn-secondary flex-1 inline-flex items-center justify-center gap-2">
              <FileText className="w-4 h-4" strokeWidth={3} /> Download PDF
            </button>
          </div>
        </section>

        {/* Next step — student is already registered for the Career Clarity Kit */}
        <section className="card-soft overflow-hidden" data-testid="next-step-section">
          <div className="grid md:grid-cols-5">
            <div className="md:col-span-3 p-7 sm:p-10">
              <div className="chip"><ShieldCheck className="w-3.5 h-3.5 text-[#2e8606]" strokeWidth={3} /> Next step</div>
              <h3 className="font-display font-black text-ink text-2xl sm:text-3xl mt-2 tracking-tight">
                Login to your dashboard.
              </h3>
              <p className="text-body mt-3">
                You&apos;re already registered for the Career Clarity Kit — jump into your dashboard to explore career worlds, tailored courses, mentor sessions, and your personalised learning path.
              </p>
              <p className="text-xs text-muted-ink mt-3">
                This is not a final career decision. It is a starting point to help you explore directions that fit your strengths, interests, and personality.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    track('dashboard_open_clicked', { lead_id: session.leadId }, session.leadId);
                    api.patch(`/leads/${session.leadId}`, { cta_clicked: true }).catch(() => {});
                    window.open(DASHBOARD_URL, '_blank', 'noopener,noreferrer');
                  }}
                  data-testid="open-dashboard-cta"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" strokeWidth={3} /> Open My Dashboard
                </button>
              </div>
            </div>
            <div className="md:col-span-2 relative card-dark !rounded-none p-7 sm:p-10">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#e59501]">Inside your dashboard</div>
              <ul className="mt-3 space-y-2 text-sm">
                <li>· Career worlds to explore</li>
                <li>· Personalised course path</li>
                <li>· Mentor session bookings</li>
                <li>· Your Career Clarity roadmap</li>
                <li>· Community & alumni access</li>
              </ul>
            </div>
          </div>

          {/* Full-report PDF download */}
          <div className="border-t border-[#E2E8F0] px-7 sm:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between bg-[#F8FAFC]">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-white shadow-sm flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-[#0055FF]" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-extrabold text-ink text-base">Prefer offline? Download the full report.</div>
                <p className="text-sm text-muted-ink mt-0.5">Your complete 9-page Career Fit Assessment magazine — save it, print it, share it with family.</p>
              </div>
            </div>
            {fullReportUrl ? (
              <a
                href={fullReportUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                onClick={() => track('full_report_downloaded', { lead_id: session.leadId }, session.leadId)}
                data-testid="download-full-report-pdf"
                className="btn-primary inline-flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <Download className="w-4 h-4" strokeWidth={3} /> Download 9-Page PDF
              </a>
            ) : pdfError ? (
              <button
                onClick={retryReport}
                disabled={pdfRetrying}
                data-testid="retry-full-report-pdf"
                className="btn-secondary inline-flex items-center justify-center gap-2 whitespace-nowrap border-2 border-[#FF5A00] text-[#FF5A00] disabled:opacity-60"
                title={pdfError.message || 'PDF generation failed — click to retry.'}
              >
                <RefreshCw className={`w-4 h-4 ${pdfRetrying ? 'animate-spin' : ''}`} strokeWidth={3} />
                {pdfRetrying ? 'Retrying…' : 'Retry PDF Generation'}
              </button>
            ) : (
              <button
                disabled
                data-testid="download-full-report-pdf-pending"
                className="btn-secondary inline-flex items-center justify-center gap-2 whitespace-nowrap opacity-60 cursor-not-allowed"
                title="Report is being generated — auto-appears in a few seconds"
              >
                <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={3} /> Preparing your PDF…
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function SectionTitle({ index, title, icon: Icon, locked = false }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-2xl bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center">
        <Icon strokeWidth={2.5} />
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-ink">Section {index}</div>
        <h3 className="font-display font-black text-ink text-2xl sm:text-3xl tracking-tight">{title}</h3>
      </div>
      {locked && (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.16em] font-bold" style={{ background: 'rgba(15,23,42,0.06)', color: '#0F172A' }}>
          <Lock className="w-3 h-3" strokeWidth={3} /> Premium
        </span>
      )}
    </div>
  );
}

function LockedSection({ locked, onUnlock, teaser, children }) {
  if (!locked) return <>{children}</>;
  return (
    <div className="relative mt-5">
      {teaser && (
        <div className="mb-3" data-testid="locked-teaser">{teaser}</div>
      )}
      <div className="relative rounded-3xl overflow-hidden ring-1 ring-[#E2E8F0]" data-testid="locked-section">
        {/* Blurred content (non-interactive) */}
        <div
          aria-hidden="true"
          className="pointer-events-none select-none"
          style={{ filter: 'blur(8px) saturate(0.85)', opacity: 0.55, maxHeight: 320, overflow: 'hidden' }}
        >
          {children}
        </div>
        {/* Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
             style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.9) 60%, #ffffff 100%)' }}>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #0F172A, #4C1D95)' }}>
            <Lock className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="mt-3 font-display font-black text-ink text-lg sm:text-xl tracking-tight">This section is locked</div>
          <p className="text-xs sm:text-sm text-muted-ink mt-1 max-w-sm">Unlock the full Career Fit Report to see this section — plus your detailed personality breakdown, career explanations, and personalised learning path.</p>
          <button
            onClick={onUnlock}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-display font-black text-sm text-[#0F172A] shadow-[0_4px_16px_rgba(255,214,0,0.4)] hover:scale-[1.02] active:scale-100 transition-transform"
            style={{ background: 'linear-gradient(90deg, #FFD600, #FFB800)' }}
          >
            <Unlock className="w-3.5 h-3.5" strokeWidth={3} /> Unlock Full Report
          </button>
        </div>
      </div>
    </div>
  );
}
